import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faList, 
  faSitemap, 
  faArrowRight,
  faCogs,
  faRocket,
  faEye,
  faEdit,
  faPlay,
  faChevronDown,
  faChevronRight,
  faCheckCircle,
  faExclamationCircle,
  faPause,
  faSpinner,
  faDesktop,
  faRobot,
  faUser,
  faCog,
  faProjectDiagram,
  faClock,
  faCalendar,
  faSearch,
  faFilter,
  faFileCode,
  faUpload,
  faDownload,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import MainLayout from '../Layout/MainLayout';
import { workflowAPI } from '../../utils/api';
import YamlWorkflowEditor from './YamlWorkflowEditor';
import WorkflowChatDialog from './WorkflowChatDialog';
import UniversalStepManager from './UniversalStepManager';
import WorkflowViewerModal from './WorkflowViewerModal';
import { sanitizeSvg } from '../../utils/sanitize';

// Step type styling - consistent with existing components
const STEP_TYPE_STYLES = {
  UITemplate: {
    icon: faDesktop,
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-100 dark:bg-green-900/20',
    textColor: 'text-green-800 dark:text-green-200'
  },
  AI: {
    icon: faRobot,
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/20',
    textColor: 'text-purple-800 dark:text-purple-200'
  },
  Human: {
    icon: faUser,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/20',
    textColor: 'text-blue-800 dark:text-blue-200'
  },
  Backend: {
    icon: faCog,
    color: 'from-gray-600 to-slate-600',
    bgColor: 'bg-gray-100 dark:bg-gray-900/20',
    textColor: 'text-gray-800 dark:text-gray-200'
  }
};

// Enhanced Mini Flow Diagram Component
const FlowDiagramPreview = ({ workflowYaml, isExpanded }) => {
  const [parsedWorkflow, setParsedWorkflow] = useState(null);

  useEffect(() => {
    if (workflowYaml && isExpanded) {
      try {
        const yaml = require('js-yaml');
        const parsed = yaml.load(workflowYaml);
        setParsedWorkflow(parsed);
      } catch (error) {
        console.error('Failed to parse workflow YAML:', error);
      }
    }
  }, [workflowYaml, isExpanded]);

  if (!isExpanded || !parsedWorkflow || !parsedWorkflow.steps) {
    return (
      <div className="text-center py-6 text-gray-400 dark:text-gray-500">
        <FontAwesomeIcon icon={faProjectDiagram} className="text-2xl mb-2" />
        <p className="text-sm">Click to view flow diagram</p>
      </div>
    );
  }

  const steps = Object.entries(parsedWorkflow.steps).slice(0, 6); // Show first 6 steps
  const stepCount = Object.keys(parsedWorkflow.steps).length;

  return (
    <div className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-xl">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          📊 Workflow Flow
        </h4>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {stepCount} steps • Start: {parsedWorkflow.start_step || 'First Step'}
        </p>
      </div>

      <div className="space-y-3">
        {steps.map(([stepName, stepConfig], index) => {
          const style = STEP_TYPE_STYLES[stepConfig.type] || STEP_TYPE_STYLES.AI;
          const isStart = index === 0;
          
          return (
            <div key={stepName} className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${style.color} flex items-center justify-center shadow-sm ${isStart ? 'ring-2 ring-green-400' : ''}`}>
                <FontAwesomeIcon icon={style.icon} className="text-white text-xs" />
                {isStart && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">🚀</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {stepName}
                </div>
                <div className={`text-xs ${style.textColor} truncate`}>
                  {stepConfig.type}
                </div>
              </div>
              {stepConfig.on && Object.keys(stepConfig.on).length > 0 && (
                <div className="text-xs text-gray-400">
                  {Object.keys(stepConfig.on).length} action{Object.keys(stepConfig.on).length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          );
        })}
        
        {stepCount > 6 && (
          <div className="text-xs text-center text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-600">
            +{stepCount - 6} more steps
          </div>
        )}
      </div>
    </div>
  );
};

// Professional Flowchart Workflow Diagram Component using Viz.js
const WorkflowFlowDiagram = ({ workflowYaml, isFullscreen = false }) => {
  const [parsedWorkflow, setParsedWorkflow] = useState(null);
  const [svgContent, setSvgContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (workflowYaml && window.jsyaml && window.Viz) {
      renderWorkflowVisualization(workflowYaml);
    }
  }, [workflowYaml]);

  const renderWorkflowVisualization = async (yamlContent) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Parse the YAML
      const workflow = window.jsyaml.load(yamlContent);
      setParsedWorkflow(workflow);
      
      // Generate DOT language string for professional flowchart
      let dotString = `
        digraph G {
          // Graph attributes for professional appearance
          rankdir="TB";
          splines="ortho";
          concentrate="true";
          bgcolor="transparent";
          nodesep="0.8";
          ranksep="1.2";
          
          // Node attributes - modern card-like appearance
          node [
            shape="record", 
            style="rounded,filled", 
            fillcolor="#f8fafc", 
            color="#334155", 
            fontname="Inter", 
            fontsize="12",
            penwidth="2",
            margin="0.3,0.2"
          ];
          
          // Edge attributes - clean modern lines
          edge [
            fontname="Inter", 
            fontsize="10", 
            color="#64748b",
            fontcolor="#475569",
            penwidth="2",
            arrowsize="0.8"
          ];

          // Special start/end nodes with distinct styling
          start [
            label="🚀 START", 
            shape="ellipse", 
            style="filled", 
            fillcolor="#dcfce7", 
            color="#16a34a",
            fontcolor="#16a34a",
            fontsize="14",
            fontweight="bold"
          ];
          
          end [
            label="✅ END", 
            shape="ellipse", 
            style="filled", 
            fillcolor="#fee2e2", 
            color="#dc2626",
            fontcolor="#dc2626",
            fontsize="14",
            fontweight="bold"
          ];
      `;

      // Add all defined steps as nodes with enhanced styling
      const steps = workflow.steps || {};
      for (const stepKey in steps) {
        const stepData = steps[stepKey];
        const stepType = stepData.type || 'AI';
        
        // Choose colors based on step type
        let fillColor, borderColor, icon;
        switch (stepType) {
          case 'AI':
            fillColor = '#f3e8ff';
            borderColor = '#8b5cf6';
            icon = '🤖';
            break;
          case 'UITemplate':
            fillColor = '#ecfdf5';
            borderColor = '#10b981';
            icon = '🖥️';
            break;
          case 'Human':
            fillColor = '#eff6ff';
            borderColor = '#3b82f6';
            icon = '👤';
            break;
          case 'Backend':
            fillColor = '#f9fafb';
            borderColor = '#6b7280';
            icon = '⚙️';
            break;
          default:
            fillColor = '#f8fafc';
            borderColor = '#64748b';
            icon = '📋';
        }
        
        const nodeLabel = `{${icon} ${stepKey}|${stepData.name.replace(/"/g, '\\"')}|${stepType}}`;
        dotString += `    "${stepKey}" [
          label="${nodeLabel}", 
          fillcolor="${fillColor}", 
          color="${borderColor}",
          fontcolor="${borderColor}"
        ];\n`;
      }

      // Connect the start node to the first step
      const startNode = workflow.flow?.start;
      if (startNode && steps[startNode]) {
        dotString += `    start -> "${startNode}" [color="#16a34a", penwidth="3"];\n`;
      }

      // Add all the edges based on the actions with enhanced styling
      for (const stepKey in steps) {
        const actions = steps[stepKey]?.actions;
        if (actions) {
          for (const actionLabel in actions) {
            const targetStep = actions[actionLabel];
            
            // Style different action types
            let edgeColor = '#64748b';
            let edgeStyle = '';
            
            if (actionLabel.includes('error') || actionLabel.includes('fail')) {
              edgeColor = '#dc2626';
              edgeStyle = ', style="dashed"';
            } else if (actionLabel.includes('success') || actionLabel.includes('complete')) {
              edgeColor = '#16a34a';
              edgeStyle = ', penwidth="3"';
            }
            
            if (steps[targetStep] || targetStep === 'end') {
              dotString += `    "${stepKey}" -> "${targetStep}" [
                label="${actionLabel.replace(/_/g, ' ')}", 
                color="${edgeColor}"
                ${edgeStyle}
              ];\n`;
            } else {
              // Handle undefined or external target steps
              const nodeLabel = `{⚠️ ${targetStep}|External Step|Unknown}`;
              dotString += `    "${targetStep}" [
                label="${nodeLabel}", 
                fillcolor="#fef3c7", 
                color="#d97706",
                fontcolor="#92400e",
                style="dashed,filled"
              ];\n`;
              dotString += `    "${stepKey}" -> "${targetStep}" [
                label="${actionLabel.replace(/_/g, ' ')}", 
                color="#d97706",
                style="dashed"
              ];\n`;
            }
          }
        }
      }

      dotString += `}`; // Close the DOT string definition

      // Render with Viz.js
      const viz = new window.Viz();
      const svgElement = await viz.renderSVGElement(dotString);
      
      // Style the SVG for responsive display
      svgElement.setAttribute('width', '100%');
      svgElement.setAttribute('height', 'auto');
      svgElement.style.maxWidth = '100%';
      svgElement.style.height = 'auto';
      
      setSvgContent(svgElement.outerHTML);
      setIsLoading(false);
      
    } catch (error) {
      console.error('Error rendering workflow visualization:', error);
      setError(error.message);
      setIsLoading(false);
    }
  };

  if (!workflowYaml) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <FontAwesomeIcon icon={faSitemap} className="text-4xl mb-2" />
        <p>No workflow data available</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-lg text-gray-600 dark:text-gray-400">Generating Flowchart...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">
          <FontAwesomeIcon icon={faExclamationCircle} className="text-4xl mb-2" />
          <h3 className="text-lg font-semibold">Failed to Generate Flowchart</h3>
          <p className="text-sm mt-2 text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Zoom and pan functionality
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoomLevel(prev => Math.min(Math.max(prev * delta, 0.1), 5));
    }
  };

  return (
    <div className="workflow-flowchart-container h-full flex flex-col">
      {/* Workflow Header */}
      {parsedWorkflow && (
        <div className="flex-shrink-0 mb-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {parsedWorkflow.name || 'Workflow Diagram'}
              </h3>
              {parsedWorkflow.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {parsedWorkflow.description}
                </p>
              )}
              {parsedWorkflow.version && (
                <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                  Version {parsedWorkflow.version}
                </span>
              )}
            </div>
            
            {/* Zoom Controls */}
            {isFullscreen && (
              <div className="flex items-center space-x-2">
                <div className="flex items-center bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 p-1">
                  <button
                    onClick={handleZoomOut}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                    title="Zoom Out"
                  >
                    <span className="text-lg font-bold">-</span>
                  </button>
                  <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[60px] text-center">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                    title="Zoom In"
                  >
                    <span className="text-lg font-bold">+</span>
                  </button>
                  <div className="border-l border-gray-300 dark:border-gray-500 mx-1 h-6"></div>
                  <button
                    onClick={handleZoomReset}
                    className="px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                    title="Reset Zoom"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Professional Flowchart Display */}
      <div 
        className="flex-1 flowchart-container bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden relative"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{
          cursor: isDragging ? 'grabbing' : isFullscreen ? 'grab' : 'default'
        }}
      >
        <div
          className="flowchart-svg-container w-full h-full flex items-center justify-center"
          style={{
            transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgContent) }}
        />
        
        {/* Pan/Zoom Instructions */}
        {isFullscreen && (
          <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white text-xs px-3 py-2 rounded-lg">
            <div className="space-y-1">
              <div>• Drag to pan</div>
              <div>• Ctrl+Scroll to zoom</div>
              <div>• Use controls to zoom</div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      {!isFullscreen && (
        <div className="flex-shrink-0 mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          <p>Professional flowchart powered by Viz.js</p>
        </div>
      )}
    </div>
  );
};

// Enhanced Workflow Card Component
const WorkflowCardV2 = ({ workflow, onAction, isExpanded, onToggleExpand }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'draft':
        return 'bg-gray-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return faCheckCircle;
      case 'paused':
        return faPause;
      case 'draft':
        return faEdit;
      case 'error':
        return faExclamationCircle;
      default:
        return faSpinner;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const status = workflow.status || (workflow.is_active ? 'active' : 'draft');

  return (
    <motion.div
      id={`workflow-${workflow.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      {/* Workflow Header */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <FontAwesomeIcon icon={faSitemap} className="text-white text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                {workflow.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {workflow.description}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Workflow Type Badge */}
            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
              workflow.workflow_type === 'yaml' 
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300' 
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
            }`}>
              {workflow.workflow_type === 'yaml' ? '📄 YAML' : '🔧 Builder'}
            </span>
            
            {/* Status Badge */}
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(status)} text-white flex items-center space-x-1 shadow-sm`}>
              <FontAwesomeIcon icon={getStatusIcon(status)} />
              <span className="capitalize">{status}</span>
            </span>
          </div>
        </div>

        {/* Workflow Meta */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <FontAwesomeIcon icon={faCalendar} />
            <span>Created: {formatDate(workflow.created_at)}</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <FontAwesomeIcon icon={faClock} />
            <span>Updated: {formatDate(workflow.updated_at)}</span>
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => onToggleExpand(workflow.id)}
          className="w-full flex items-center justify-center space-x-2 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors border-t border-gray-100 dark:border-gray-700"
        >
          <FontAwesomeIcon 
            icon={isExpanded ? faChevronDown : faChevronRight} 
            className="text-sm"
          />
          <span className="text-sm font-medium">
            {isExpanded ? 'Hide Details' : 'Show Flow Diagram'}
          </span>
        </button>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-gray-100 dark:border-gray-700"
          >
            {/* Flow Diagram */}
            <div className="p-6">
              <FlowDiagramPreview workflowYaml={workflow.workflow_yaml} isExpanded={isExpanded} />
            </div>

            {/* Actions */}
            <div className="px-6 pb-6">
              <div className={`grid gap-3 mb-3 ${workflow.workflow_type === 'yaml' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <button
                  onClick={() => onAction('view', workflow)}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>View</span>
                </button>
                
                {/* Only show Edit button for non-YAML workflows */}
                {workflow.workflow_type !== 'yaml' && (
                  <button
                    onClick={() => onAction('edit', workflow)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2 font-medium"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    <span>Edit</span>
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                {status === 'active' && (
                  <button
                    onClick={() => onAction('run', workflow)}
                    className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center space-x-2 font-medium text-sm"
                  >
                    <FontAwesomeIcon icon={faPlay} />
                    <span>Run</span>
                  </button>
                )}
                
                <button
                  onClick={() => onAction('editYaml', workflow)}
                  className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center justify-center space-x-2 font-medium text-sm"
                >
                  <FontAwesomeIcon icon={faFileCode} />
                  <span>YAML</span>
                </button>
                
                <button
                  onClick={() => onAction('downloadYaml', workflow)}
                  className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center justify-center space-x-2 font-medium text-sm"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const WorkflowsV2 = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);
  const [error, setError] = useState(null);
  const [showYamlEditor, setShowYamlEditor] = useState(false);
  const [editingYamlWorkflow, setEditingYamlWorkflow] = useState(null);
  
  // Workflow Chat Dialog State
  const [showWorkflowChat, setShowWorkflowChat] = useState(false);
  const [chatWorkflow, setChatWorkflow] = useState(null);
  const [viewingWorkflow, setViewingWorkflow] = useState(null);
  const [showWorkflowViewer, setShowWorkflowViewer] = useState(false);

  // Workflow Runner State (Universal UI/Chat Manager)
  const [showWorkflowRunner, setShowWorkflowRunner] = useState(false);
  const [runningWorkflow, setRunningWorkflow] = useState(null);

  // Fetch workflows from API
  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await workflowAPI.getWorkflows();
      setWorkflows(response.workflows || []);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      setError('Failed to load workflows');
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.description.toLowerCase().includes(searchTerm.toLowerCase());
    const workflowStatus = workflow.status || (workflow.is_active ? 'active' : 'draft');
    const matchesStatus = statusFilter === 'all' || workflowStatus === statusFilter;
    const matchesType = typeFilter === 'all' || (workflow.workflow_type || 'builder') === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleCreateWorkflow = () => {
    navigate('/workflows-v2/builder');
  };

  const handleViewWorkflow = (workflow) => {
    // Show workflow viewer modal for all workflows with YAML content
    if (workflow.workflow_yaml) {
      setViewingWorkflow(workflow);
      setShowWorkflowViewer(true);
    } else {
      // For workflows without YAML, expand the card
      setExpandedWorkflow(workflow.id);
      // Scroll to the workflow card
      setTimeout(() => {
        const element = document.getElementById(`workflow-${workflow.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleEditWorkflow = (workflow) => {
    navigate(`/workflows-v2/builder?id=${workflow.id}`);
  };

  const handleWorkflowAction = (action, workflow) => {
    switch (action) {
      case 'view':
        handleViewWorkflow(workflow);
        break;
      case 'edit':
        handleEditWorkflow(workflow);
        break;
      case 'run':
        console.log('Running V2 workflow:', workflow.id);
        // ✅ NEW UNIVERSAL WORKFLOW RUNNER with backward compatibility
        handleRunWorkflow(workflow);
        break;
      case 'editYaml':
        handleEditYamlWorkflow(workflow);
        break;
      case 'downloadYaml':
        handleDownloadYaml(workflow);
        break;
      default:
        break;
    }
  };

  const handleToggleExpand = (workflowId) => {
    setExpandedWorkflow(expandedWorkflow === workflowId ? null : workflowId);
  };

  // ==================== UNIVERSAL WORKFLOW RUNNER ====================
  // Handles both UI-first and AI-first workflows with backward compatibility

  const handleRunWorkflow = async (workflow) => {
    try {
      console.log('🚀 Starting universal workflow runner for:', workflow.name);

      // Show the universal step manager modal
      setRunningWorkflow(workflow);
      setShowWorkflowRunner(true);

    } catch (error) {
      console.error('❌ Failed to start workflow:', error);

      // ✅ FALLBACK: If anything fails, use existing chat navigation
      toast.error('Failed to determine workflow type. Redirecting to chat...');
      setTimeout(() => {
        navigate(`/workflows/${workflow.id}/chat?name=${encodeURIComponent(workflow.name)}`);
      }, 1500);
    }
  };

  const handleWorkflowRunnerChatRedirect = (runId = null) => {
    console.log('🔄 Redirecting to chat interface with runId:', runId);

    const workflowId = runningWorkflow?.id;

    // Budget Agent (28) - no chat page, DON'T close modal - stay in workflow runner
    if (workflowId === 28) {
      console.log('📊 Budget Agent - no chat redirect, keeping modal open');
      // Don't close modal, don't navigate - UniversalStepManager handles polling
      return;
    }

    // Close the runner modal
    setShowWorkflowRunner(false);
    setRunningWorkflow(null);

    // Sale Order workflow (15) - redirect to dedicated chat page
    // Note: Bill Processing (14) doesn't return transition_to:'chat', so never hits this
    // Note: Bank Agent (16) uses polling branch, never hits this redirect
    if (workflowId === 15) {
      const saleUrl = runId ? `/sale-order-agent?runId=${runId}` : '/sale-order-agent';
      console.log('📦 Sale Order (NetSuite) redirect to:', saleUrl);
      navigate(saleUrl);
      return;
    }

    // SF Sale Order workflow (Salesforce) (21) - redirect to dedicated chat page
    if (workflowId === 21) {
      const sfSaleUrl = runId ? `/sale-order-sf?runId=${runId}` : '/sale-order-sf';
      console.log('🔵 SF Sale Order (Salesforce) redirect to:', sfSaleUrl);
      navigate(sfSaleUrl);
      return;
    }

    // Bill Processing WebSocket (22) - redirect to dedicated WebSocket chat page
    if (workflowId === 22) {
      const billWsUrl = runId ? `/bill-ws-chat/${runId}` : '/bill-ws-chat';
      console.log('🔌 Bill Processing WS redirect to:', billWsUrl);
      navigate(billWsUrl);
      return;
    }

    // Default: Navigate to generic workflow chat
    const chatUrl = runId
      ? `/workflows/${workflowId}/chat?run_id=${runId}&name=${encodeURIComponent(runningWorkflow?.name || '')}`
      : `/workflows/${workflowId}/chat?name=${encodeURIComponent(runningWorkflow?.name || '')}`;

    navigate(chatUrl);
  };

  const handleWorkflowRunnerComplete = (result) => {
    console.log('✅ Workflow completed:', result);

    // Close the runner modal
    setShowWorkflowRunner(false);
    setRunningWorkflow(null);

    // Show success message
    toast.success(`Workflow "${runningWorkflow?.name}" completed successfully!`);

    // Optionally refresh workflows list
    fetchWorkflows();
  };

  const handleWorkflowRunnerError = (error) => {
    console.error('❌ Workflow runner error:', error);

    // Show error toast but keep modal open for retry
    toast.error('Workflow execution failed. Please try again.');
  };

  // YAML-specific handlers
  const handleCreateYamlWorkflow = () => {
    setEditingYamlWorkflow(null);
    setShowYamlEditor(true);
  };

  const handleEditYamlWorkflow = async (workflow) => {
    try {
      const yamlData = await workflowAPI.exportYaml(workflow.id);
      setEditingYamlWorkflow({
        ...workflow,
        yaml: yamlData.workflow_yaml
      });
      setShowYamlEditor(true);
    } catch (error) {
      console.error('Failed to load workflow YAML:', error);
      toast.error('Failed to load workflow for editing');
    }
  };

  const handleDownloadYaml = async (workflow) => {
    try {
      const yamlData = await workflowAPI.exportYaml(workflow.id);
      const blob = new Blob([yamlData.workflow_yaml], { type: 'text/yaml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${workflow.name.replace(/\s+/g, '-').toLowerCase()}.yaml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Workflow YAML downloaded');
    } catch (error) {
      console.error('Failed to download YAML:', error);
      toast.error('Failed to download workflow YAML');
    }
  };

  const handleYamlSave = (response) => {
    setShowYamlEditor(false);
    setEditingYamlWorkflow(null);
    fetchWorkflows(); // Refresh the list
    toast.success('Workflow saved successfully!');
  };

  const handleYamlCancel = () => {
    setShowYamlEditor(false);
    setEditingYamlWorkflow(null);
  };

  // Action cards for main actions
  const actionCards = [
    {
      id: 'create-workflow',
      title: 'Workflow Builder',
      description: 'Build automated workflows with our visual designer - Currently under maintenance',
      icon: faCogs,
      primaryIcon: faCogs,
      color: 'from-gray-400 to-gray-500',
      buttonText: 'Under Maintenance',
      action: () => toast.error('Workflow Builder is currently under maintenance. Please use YAML workflows for now.'),
      features: [
        'Visual workflow designer',
        'Step validation & auto-save',
        'Flow diagram preview',
        'Import/export workflows'
      ],
      isDisabled: true
    },
    {
      id: 'yaml-workflows',
      title: 'YAML AI Workflows',
      description: 'Create and manage AI workflows using YAML - perfect for power users',
      icon: faFileCode,
      primaryIcon: faRobot,
      color: 'from-purple-500 to-pink-600',
      buttonText: 'Create YAML',
      action: handleCreateYamlWorkflow,
      features: [
        'Direct YAML editing',
        'AI step validation',
        'Real-time syntax checking',
        'Template library'
      ]
    }
  ];

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl">
                <FontAwesomeIcon icon={faSitemap} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                  Workflows v2
                </h1>
                <p className="text-gray-600 dark:text-gray-300 text-lg mt-1">
                  Enhanced workflow management with visual flow diagrams
                </p>
              </div>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/20 px-4 py-2 rounded-xl">
              <span className="text-purple-700 dark:text-purple-300 text-sm font-semibold">
                ✨ NEW UI
              </span>
            </div>
          </div>
        </motion.div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {actionCards.map((card, index) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={!card.isDisabled ? { 
                scale: 1.02, 
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
              } : {}}
              className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden group ${card.isDisabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={card.action}
            >
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${card.color} p-6 relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                  <FontAwesomeIcon icon={card.primaryIcon} className="text-white text-8xl transform rotate-12" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <FontAwesomeIcon icon={card.icon} className="text-white text-xl" />
                    </div>
                    <div className="text-white/80 group-hover:text-white transition-colors">
                      <FontAwesomeIcon icon={faRocket} className="text-lg" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {card.title}
                  </h2>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-300 text-lg mb-6 leading-relaxed">
                  {card.description}
                </p>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {card.features.map((feature, featureIndex) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + (featureIndex * 0.1), duration: 0.4 }}
                      className="flex items-center space-x-3 text-gray-700 dark:text-gray-300"
                    >
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"></div>
                      <span className="text-sm font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Action Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full bg-gradient-to-r ${card.color} text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2 group-hover:shadow-2xl`}
                >
                  <span>{card.buttonText}</span>
                  <FontAwesomeIcon 
                    icon={faArrowRight} 
                    className="text-sm transform group-hover:translate-x-1 transition-transform duration-300" 
                  />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Workflows Management Section */}
        <div id="workflows-grid">
          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <FontAwesomeIcon 
                    icon={faSearch} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search workflows..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[300px]"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="draft">Draft</option>
                    <option value="error">Error</option>
                  </select>
                  
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">All Types</option>
                    <option value="builder">🔧 Builder</option>
                    <option value="yaml">📄 YAML</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg">
                  {filteredWorkflows.length} of {workflows.length} workflows
                </div>
              </div>
            </div>
          </motion.div>

          {/* Workflows Grid */}
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-purple-600" />
                  <span className="text-lg text-gray-600 dark:text-gray-300">Loading workflows...</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {error}
                </h3>
                <button
                  onClick={fetchWorkflows}
                  className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <div className="text-center py-16">
                <FontAwesomeIcon icon={faSitemap} className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'No workflows found' 
                    : 'No workflows yet'
                  }
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Try adjusting your search terms or filters' 
                    : 'Create your first workflow to get started with automation'
                  }
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <button
                    onClick={handleCreateWorkflow}
                    className="px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 mx-auto"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create Your First Workflow</span>
                  </button>
                )}
              </div>
            ) : (
              filteredWorkflows.map((workflow) => (
                <WorkflowCardV2
                  key={workflow.id}
                  workflow={workflow}
                  isExpanded={expandedWorkflow === workflow.id}
                  onToggleExpand={handleToggleExpand}
                  onAction={handleWorkflowAction}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Workflow Viewer Modal */}
      <WorkflowViewerModal
        workflow={viewingWorkflow}
        isOpen={showWorkflowViewer}
        onClose={() => {
          setShowWorkflowViewer(false);
          setViewingWorkflow(null);
        }}
      />

      {/* YAML Editor Modal */}
      {showYamlEditor && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleYamlCancel();
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-6xl w-full h-[90vh] overflow-hidden flex flex-col"
          >
            <YamlWorkflowEditor
              initialYaml={editingYamlWorkflow?.yaml || ''}
              workflowId={editingYamlWorkflow?.id || null}
              onSave={handleYamlSave}
              onCancel={handleYamlCancel}
            />
          </motion.div>
        </motion.div>
      )}

      {/* Universal Workflow Runner Modal */}
      {showWorkflowRunner && runningWorkflow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {runningWorkflow.name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {runningWorkflow.description || 'Interactive Workflow Execution'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowWorkflowRunner(false);
                    setRunningWorkflow(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Close"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <UniversalStepManager
                workflowId={runningWorkflow.id}
                onChatRedirect={handleWorkflowRunnerChatRedirect}
                onWorkflowComplete={handleWorkflowRunnerComplete}
                onError={handleWorkflowRunnerError}
                onCancel={() => {
                  setShowWorkflowRunner(false);
                  setRunningWorkflow(null);
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Workflow Chat Dialog */}
      <WorkflowChatDialog
        isOpen={showWorkflowChat}
        onClose={() => {
          setShowWorkflowChat(false);
          setChatWorkflow(null);
        }}
        workflow={chatWorkflow}
      />
    </MainLayout>
  );
};

export default WorkflowsV2;